import API from '../config/api';
import { useEffect, useState } from 'react';

import axios from 'axios';

/*
  Real customer ratings for product listings.

  Every card in the app used to show a hardcoded "4.8". This hook loads the
  per-product averages from the reviews collection in a single request and
  hands back a lookup. Products with no reviews are absent from the response,
  so `getRating` reports 0 for them.

  Several listings can be on screen at once (ProductSection + SuggestedSection
  + QuickViewModal on the home page), so the in-flight request is shared at
  module level — they all resolve from one network call.
*/

const EMPTY_RATING = { averageRating: 0, totalReviews: 0 };

let ratingsCache = null;

let inFlight = null;

const loadRatings = () => {
  if (ratingsCache) {
    return Promise.resolve(ratingsCache);
  }

  if (!inFlight) {
    inFlight = axios
      .get(`${API}/reviews/summary`)
      .then(({ data }) => {
        const byProduct = {};

        data.forEach((item) => {
          byProduct[item.productId] = {
            averageRating: item.averageRating,
            totalReviews: item.totalReviews
          };
        });

        ratingsCache = byProduct;

        return byProduct;
      })
      .catch((error) => {
        console.log(error);

        /* Fail soft — cards fall back to "no reviews yet" */
        return {};
      })
      .finally(() => {
        inFlight = null;
      });
  }

  return inFlight;
};

/* Call after a review is added/deleted so the next read is fresh */
export const clearRatingsCache = () => {
  ratingsCache = null;
};

export function useProductRatings() {
  const [ratings, setRatings] = useState(ratingsCache || {});

  useEffect(() => {
    let active = true;

    loadRatings().then((data) => {
      if (active) {
        setRatings(data);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  const getRating = (productId) => ratings[String(productId)] || EMPTY_RATING;

  return { ratings, getRating };
}

export default useProductRatings;
